import { useState, useEffect, useCallback, useRef } from 'react';

// General dropdown hook that can be used across modules
interface UseDropdownsOptions {
  module: string;
  categories?: string[];
  autoLoad?: boolean;
  serviceMethod?: () => Promise<any>; // Allow custom service method
}

interface DropdownResult {
  data: Record<string, any>;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  
  // Generic helper functions for dropdown operations
  getOptionByValue: (category: string, value: string) => any;
  getOptionByLabel: (category: string, label: string) => any;
  searchOptions: (category: string, searchTerm: string) => any[];
}

export const useDropdowns = ({ 
  module, 
  categories, 
  autoLoad = true,
  serviceMethod
}: UseDropdownsOptions): DropdownResult => {
  const [data, setData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Use ref to store current data for stable helper functions
  const dataRef = useRef<Record<string, any>>({});
  
  // Update ref whenever data changes
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const loadDropdowns = useCallback(async () => {
    if (!serviceMethod) {
      setError(`No service method provided for module: ${module}`);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const result = await serviceMethod();
      
      if (result && result.success) {
        setData(result.data || {});
      } else {
        throw new Error(result?.message || 'Failed to load dropdown options');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dropdowns';
      setError(errorMessage);
      console.error(`❌ Error loading dropdowns for ${module}:`, err);
    } finally {
      setLoading(false);
    }
  }, [serviceMethod]);

  // Generic helper function to find option by value - uses ref for stable reference
  const getOptionByValue = useCallback((category: string, value: string) => {
    const categoryData = dataRef.current[category] || [];
    return categoryData.find((option: any) => option.value === value);
  }, []);

  // Generic helper function to find option by label - uses ref for stable reference
  const getOptionByLabel = useCallback((category: string, label: string) => {
    const categoryData = dataRef.current[category] || [];
    return categoryData.find((option: any) => option.label === label);
  }, []);

  // Generic helper function to search within a category - uses ref for stable reference
  const searchOptions = useCallback((category: string, searchTerm: string) => {
    const categoryData = dataRef.current[category] || [];
    return categoryData.filter((option: any) => 
      option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.value.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, []);

  useEffect(() => {
    if (autoLoad && serviceMethod) {
      loadDropdowns();
    }
  }, [autoLoad, loadDropdowns]);

  return { 
    data, 
    loading, 
    error, 
    reload: loadDropdowns,
    getOptionByValue,
    getOptionByLabel,
    searchOptions
  };
};

export default useDropdowns;

